import { useEffect, useRef, useReducer, memo, useCallback } from 'react';
import qs from 'query-string';
import { ChevronRight, ChevronLeft } from "lucide-react";

// Reducer for managing scroll state
const scrollReducer = (state, action) => {
	switch (action.type) {
		case 'SET_SCROLL_STATE':
			// Only update if values actually changed
			if (state.canScrollLeft === action.canScrollLeft && 
			    state.canScrollRight === action.canScrollRight) {
				return state;
			}
			return {
				canScrollLeft: action.canScrollLeft,
				canScrollRight: action.canScrollRight
			};
		default:
			return state;
	}
};

// Visual Filter Tab Component - extracted outside to prevent recreation
const VisualFilterTab = memo(({ item, isActive, onClick }) => {
	const handleClick = useCallback((e) => {
		e.preventDefault();
		e.stopPropagation();
		onClick(item);
	}, [item, onClick]);

	return (
		<div
			data-filter-item-id={item?.item_id}
			className={`
				flex items-center justify-center px-2 py-2 cursor-pointer relative
				font-akkurat whitespace-nowrap
				${isActive
					? `text-mobile-b1-body-1 lg:text-web-s3-section-content !border-b-[3px] border-[#292425]`
					: `text-mobile-b2-body-2 lg:text-web-s4-section-content-2 border-b-3 border-transparent`
				}
			`}
			onClick={handleClick}
			style={{ 
				pointerEvents: 'auto',
				userSelect: 'none',
				WebkitUserSelect: 'none',
				MozUserSelect: 'none',
				msUserSelect: 'none'
			}}
		>
			<span>
				{item?.itemName}
			</span>
		</div>
	);
});

const FilterCarousel = memo(function FilterCarousel({
	carouselData,
	handleVisualFilterClick,
	selectedVisualFilter,
	showCarousel,
	slug,
}) {
	const [scrollState, dispatchScroll] = useReducer(scrollReducer, {
		canScrollLeft: false,
		canScrollRight: false
	});
	
	const scrollContainerRef = useRef(null);
	const isInitializedRef = useRef(false);
	const carouselItemsRef = useRef(null);
	const hasScrolledToSelectedRef = useRef(false);
	
	const carouselItems = carouselData?.items;

	// Static "All" tab data - no need for memoization
	const allTabData = {
		item_id: 'all',
		itemName: 'All',
		itemSlug: 'all',
		itemType: 'filter',
		tags: null,
		tagType: 'clear'
	};

	// Build tabs data directly - no memoization needed
	const allTabsData = [allTabData, ...(carouselItems || [])];

	// Check scroll state - memoized to prevent recreation
	const checkScrollState = useCallback(() => {
		if (scrollContainerRef.current) {
			const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
			
			// CRITICAL: Check if content actually overflows the available container width
			const hasOverflow = scrollWidth > clientWidth;
			
			const newCanScrollLeft = scrollLeft > 0;
			const newCanScrollRight = hasOverflow && scrollLeft < scrollWidth - clientWidth - 1;
			
			// Dispatch to reducer which handles change detection
			dispatchScroll({
				type: 'SET_SCROLL_STATE',
				canScrollLeft: newCanScrollLeft,
				canScrollRight: newCanScrollRight
			});
		}
	}, []);

	// Initialize visual filter from URL - only run once on mount
	useEffect(() => {
		if (carouselItems && !isInitializedRef.current) {
			const parsed = qs.parse(window.location.search);
			let filterItem = null;

			// First try 'tag' parameter (for gift guide and similar collections)
			if (parsed && parsed.tag) {
				// Handle both single tag string and array of tags
				const tags = Array.isArray(parsed.tag) ? parsed.tag : [parsed.tag];

				// Find matching filter item by tags
				filterItem = carouselItems.find(
					(item) =>
						item.tags && item.tags.some((tag) => tags.includes(tag))
				);
			}

			// If no match found with tags, try 'cf' parameter
			if (!filterItem && parsed && parsed.cf) {
				filterItem = carouselItems.find(
					(item) => item.itemSlug === parsed.cf
				);
			}

			// Apply the visual filter if found
			if (filterItem && handleVisualFilterClick) {
				handleVisualFilterClick(filterItem);
				// Mark that we need to scroll to this filter on initial load
				hasScrolledToSelectedRef.current = false;
				
				// Also trigger scroll after a delay to ensure DOM is ready
				setTimeout(() => {
					if (filterItem?.item_id) {
						// Find visible container
						let visibleContainer = scrollContainerRef.current;
						if (scrollContainerRef.current) {
							const rect = scrollContainerRef.current.getBoundingClientRect();
							if (rect.width === 0 || rect.height === 0) {
								// Find visible container
								const allContainers = document.querySelectorAll('[data-filter-carousel-container="true"]');
								allContainers.forEach(container => {
									const containerRect = container.getBoundingClientRect();
									if (containerRect.width > 0 && containerRect.height > 0) {
										visibleContainer = container;
									}
								});
							}
						}
						
						if (visibleContainer) {
							const selectedTab = visibleContainer.querySelector(
								`[data-filter-item-id="${filterItem.item_id}"]`
							);
							
							if (selectedTab) {
								const scrollLeft = selectedTab.offsetLeft - (visibleContainer.offsetWidth / 2) + (selectedTab.offsetWidth / 2);
								
								visibleContainer.scrollTo({
									left: Math.max(0, scrollLeft),
									behavior: 'smooth'
								});
								hasScrolledToSelectedRef.current = true;
							}
						}
					}
				}, 500);
			}
			isInitializedRef.current = true;
		}
	}, [carouselItems, handleVisualFilterClick]);

	// Scroll to selected tab when it's set from URL (only on initial load)
	useEffect(() => {
		// Only scroll if we have a valid selected filter (not 'clear' or null)
		if (!selectedVisualFilter || selectedVisualFilter === 'clear' || selectedVisualFilter === null) {
			return;
		}

		// Only scroll on initial load from URL, not on manual clicks
		if (hasScrolledToSelectedRef.current) {
			return;
		}

		// Wait for DOM to update with the selected state
		const scrollToSelected = () => {
			const selectedItemId = selectedVisualFilter?.item_id;
			if (!selectedItemId) {
				return;
			}

			// Find all scroll containers (there are multiple for different breakpoints)
			// Find the one that's currently visible
			const allContainers = document.querySelectorAll('[data-filter-carousel-container="true"]');
			let visibleContainer = scrollContainerRef.current;
			
			// Find the visible container by checking which one is in viewport
			if (scrollContainerRef.current) {
				const rect = scrollContainerRef.current.getBoundingClientRect();
				if (rect.width === 0 || rect.height === 0) {
					// Container not visible, try to find visible one
					allContainers.forEach(container => {
						const containerRect = container.getBoundingClientRect();
						if (containerRect.width > 0 && containerRect.height > 0 && 
							containerRect.top >= 0 && containerRect.top < window.innerHeight) {
							visibleContainer = container;
						}
					});
				}
			}

			if (!visibleContainer) {
				// Retry if container not ready yet
				setTimeout(scrollToSelected, 100);
				return;
			}

			// Find the tab element with matching item_id within the visible container
			const selectedTab = visibleContainer.querySelector(
				`[data-filter-item-id="${selectedItemId}"]`
			);

			if (!selectedTab) {
				// Tab not found yet, retry after a short delay
				setTimeout(scrollToSelected, 100);
				return;
			}

			// Calculate scroll position to center the tab (or at least make it visible)
			const container = visibleContainer;
			const tabRect = selectedTab.getBoundingClientRect();
			const containerRect = container.getBoundingClientRect();
			
			// Check if tab is already visible (with some padding)
			const padding = 20;
			const isVisible = 
				tabRect.left >= (containerRect.left - padding) && 
				tabRect.right <= (containerRect.right + padding);

			if (!isVisible) {
				// Calculate scroll position to center the tab
				const scrollLeft = selectedTab.offsetLeft - (container.offsetWidth / 2) + (selectedTab.offsetWidth / 2);
				
				container.scrollTo({
					left: Math.max(0, scrollLeft),
					behavior: 'smooth'
				});
			}

			hasScrolledToSelectedRef.current = true;
		};

		// Use multiple requestAnimationFrame calls and a timeout to ensure DOM is fully updated
		requestAnimationFrame(() => {
			requestAnimationFrame(() => {
				// Add a small delay to ensure React has finished rendering
				setTimeout(scrollToSelected, 150);
			});
		});
	}, [selectedVisualFilter, carouselItems]);

	// Scroll functions - memoized to prevent recreation (renamed to avoid DOM property conflicts)
	const handleScrollLeft = useCallback(() => {
		if (scrollContainerRef.current) {
			scrollContainerRef.current.scrollBy({ left: -200, behavior: 'smooth' });
		}
	}, []);

	const handleScrollRight = useCallback(() => {
		if (scrollContainerRef.current) {
			scrollContainerRef.current.scrollBy({ left: 200, behavior: 'smooth' });
		}
	}, []);

	// Check scroll state on mount and resize
	useEffect(() => {
		// Use ResizeObserver for more accurate scroll state detection
		let resizeObserver = null;
		
		if (scrollContainerRef.current) {
			// Initial check with multiple attempts to ensure DOM is ready
			const checkInitialState = () => {
				checkScrollState();
				// Double-check after a longer delay for slow renders
				setTimeout(() => checkScrollState(), 300);
			};
			
			// Immediate check
			checkScrollState();
			// Delayed check
			setTimeout(checkInitialState, 100);
			
			// Use ResizeObserver to detect size changes
			if (typeof ResizeObserver !== 'undefined') {
				resizeObserver = new ResizeObserver(() => {
					setTimeout(() => checkScrollState(), 50);
				});
				resizeObserver.observe(scrollContainerRef.current);
			}
		}
		
		const handleResize = () => {
			setTimeout(() => checkScrollState(), 100);
		};
		
		window.addEventListener('resize', handleResize);
		return () => {
			if (resizeObserver) {
				resizeObserver.disconnect();
			}
			window.removeEventListener('resize', handleResize);
		};
	}, [checkScrollState]);

	// Track carousel items changes with ref to avoid re-renders
	useEffect(() => {
		const currentItems = carouselItems?.length || 0;
		const prevItems = carouselItemsRef.current;
		
		if (prevItems !== currentItems && currentItems > 0) {
			carouselItemsRef.current = currentItems;
			const timer = setTimeout(() => {
				checkScrollState();
			}, 100);
			return () => clearTimeout(timer);
		}
	}, [carouselItems?.length, checkScrollState]);

	// Handle visual filter click - memoized to prevent recreation
	const handleFilterClick = useCallback((item) => {
		if (!handleVisualFilterClick || typeof handleVisualFilterClick !== 'function') {
			return;
		}

		// Special handling for "All" tab
		if (item.item_id === 'all' || item.itemSlug === 'all') {
			handleVisualFilterClick('clear');
			return;
		}

		// FIXED: Don't toggle - always apply the filter, even if it's already active
		// This prevents the filter from being removed when clicked again
		handleVisualFilterClick(item);
	}, [handleVisualFilterClick]);

	if (!showCarousel || !carouselItems || carouselItems.length === 0) {
		return null;
	}

	return (
		<div className="w-full">
			{/* Mobile Layout (< 768px) - FilterSystem is hidden */}
			<div className="md:hidden">
				<div className="flex items-center w-full border-b border-[#E6E7E8] relative gap-2" 
					 style={{ maxWidth: 'calc(100vw - 32px)' }}>
					{/* Left Arrow - Only show when scrolled right */}
					{scrollState.canScrollLeft && (
						<button
							onClick={handleScrollLeft}
							className="flex-shrink-0"
						>
							<ChevronLeft className="text-[#231F20] w-4 h-4" />
						</button>
					)}
					
					{/* Scrollable Filter Container */}
					<div 
						ref={scrollContainerRef}
						data-filter-carousel-container="true"
						className="flex items-center gap-4 overflow-x-auto scrollbar-hide scroll-smooth flex-1"
						onScroll={checkScrollState}
						style={{ 
							scrollbarWidth: 'none',
							msOverflowStyle: 'none',
							minWidth: 0,
							maxWidth: '100%',
							pointerEvents: 'auto',
							zIndex: 1
						}}
					>
						{allTabsData.map((item, i) => {
							// Special handling for "All" tab - active when no filter is selected
							const isActive = item.item_id === 'all' 
								? !selectedVisualFilter || selectedVisualFilter === null || selectedVisualFilter === 'clear'
								: selectedVisualFilter && selectedVisualFilter !== 'clear' && selectedVisualFilter !== null && (
									selectedVisualFilter.item_id === item.item_id
								);
							return (
								<VisualFilterTab
									key={i}
									item={item}
									isActive={isActive}
									onClick={handleFilterClick}
								/>
							);
						})}
					</div>
					
					{/* Right Arrow - Only show when content overflows container width */}
					{scrollState.canScrollRight && (
						<button
							onClick={handleScrollRight}
							className="flex-shrink-0"
						>
							<ChevronRight className="text-[#231F20] w-4 h-4" />
						</button>
					)}
				</div>
			</div>

			{/* Tablet Layout (768px - 1024px) - FilterSystem w-72 visible */}
			<div className="hidden md:flex lg:hidden w-full">
				<div className="flex items-center w-full border-b border-[#E6E7E8] relative gap-2"
					 style={{ maxWidth: 'calc(100vw - 344px)' }}>
					{/* Left Arrow */}
					{scrollState.canScrollLeft && (
						<button
							onClick={handleScrollLeft}
							className="flex-shrink-0"
						>
							<ChevronLeft className="text-[#231F20] w-4 h-4" />
						</button>
					)}
					
					{/* Scrollable Filter Container */}
					<div 
						ref={scrollContainerRef}
						data-filter-carousel-container="true"
						className="flex items-center gap-4 overflow-x-auto scrollbar-hide scroll-smooth flex-1"
						onScroll={checkScrollState}
						style={{ 
							scrollbarWidth: 'none',
							msOverflowStyle: 'none',
							minWidth: 0,
							maxWidth: '100%',
							pointerEvents: 'auto',
							zIndex: 1
						}}
					>
						{allTabsData.map((item, i) => {
							// Special handling for "All" tab - active when no filter is selected
							const isActive = item.item_id === 'all' 
								? !selectedVisualFilter || selectedVisualFilter === null || selectedVisualFilter === 'clear'
								: selectedVisualFilter && selectedVisualFilter !== 'clear' && selectedVisualFilter !== null && (
									selectedVisualFilter.item_id === item.item_id
								);
							return (
								<VisualFilterTab
									key={i}
									item={item}
									isActive={isActive}
									onClick={handleFilterClick}
								/>
							);
						})}
					</div>
					
					{/* Right Arrow */}
					{scrollState.canScrollRight && (
						<button
							onClick={handleScrollRight}
							className="flex-shrink-0"
						>
							<ChevronRight className="text-[#231F20] w-4 h-4" />
						</button>
					)}
				</div>
			</div>

			{/* Desktop Large Layout (1024px - 1280px) - FilterSystem w-72 visible */}
			<div className="hidden lg:flex xl:hidden w-full">
				<div className="flex items-center w-full border-b border-[#E6E7E8] relative gap-2"
					style={{ maxWidth: 'calc(100vw - 24rem)' }}
				>
					{/* Left Arrow */}
					{scrollState.canScrollLeft && (
						<button
							onClick={handleScrollLeft}
							className="flex-shrink-0"
						>
							<ChevronLeft className="text-[#231F20] w-4 h-4" />
						</button>
					)}
					
					{/* Scrollable Filter Container */}
					<div 
						ref={scrollContainerRef}
						data-filter-carousel-container="true"
						className="flex items-center gap-4 overflow-x-auto scrollbar-hide scroll-smooth flex-1"
						onScroll={checkScrollState}
						style={{ 
							scrollbarWidth: 'none',
							msOverflowStyle: 'none',
							minWidth: 0,
							maxWidth: '100%',
							pointerEvents: 'auto',
							zIndex: 1
						}}
					>
						{allTabsData.map((item, i) => {
							// Special handling for "All" tab - active when no filter is selected
							const isActive = item.item_id === 'all' 
								? !selectedVisualFilter || selectedVisualFilter === null || selectedVisualFilter === 'clear'
								: selectedVisualFilter && selectedVisualFilter !== 'clear' && selectedVisualFilter !== null && (
									selectedVisualFilter.item_id === item.item_id
								);
							return (
								<VisualFilterTab
									key={i}
									item={item}
									isActive={isActive}
									onClick={handleFilterClick}
								/>
							);
						})}
					</div>
					
					{/* Right Arrow */}
					{scrollState.canScrollRight && (
						<button
							onClick={handleScrollRight}
							className="flex-shrink-0"
						>
							<ChevronRight className="text-[#231F20] w-4 h-4" />
						</button>
					)}
				</div>
			</div>

			{/* Desktop XL Layout (≥ 1280px) - FilterSystem xl:w-80 visible */}
			<div className="hidden xl:flex w-full">
				<div className="flex items-center w-full border-b border-[#E6E7E8] relative gap-2"
					style={{ maxWidth: 'calc(100vw - 26rem)' }}
				>
					{/* Left Arrow */}
					{scrollState.canScrollLeft && (
						<button
							onClick={handleScrollLeft}
							className="flex-shrink-0"
						>
							<ChevronLeft className="text-[#231F20] w-4 h-4" />
						</button>
					)}
					
					{/* Scrollable Filter Container */}
					<div 
						ref={scrollContainerRef}
						data-filter-carousel-container="true"
						className="flex items-center gap-4 overflow-x-auto scrollbar-hide scroll-smooth flex-1"
						onScroll={checkScrollState}
						style={{ 
							scrollbarWidth: 'none',
							msOverflowStyle: 'none',
							minWidth: 0,
							maxWidth: '100%',
							pointerEvents: 'auto',
							zIndex: 1
						}}
					>
						{allTabsData.map((item, i) => {
							// Special handling for "All" tab - active when no filter is selected
							const isActive = item.item_id === 'all' 
								? !selectedVisualFilter || selectedVisualFilter === null || selectedVisualFilter === 'clear'
								: selectedVisualFilter && selectedVisualFilter !== 'clear' && selectedVisualFilter !== null && (
									selectedVisualFilter.item_id === item.item_id
								);
							return (
								<VisualFilterTab
									key={i}
									item={item}
									isActive={isActive}
									onClick={handleFilterClick}
								/>
							);
						})}
					</div>
					
					{/* Right Arrow */}
					{scrollState.canScrollRight && (
						<button
							onClick={handleScrollRight}
							className="flex-shrink-0"
						>
							<ChevronRight className="text-[#231F20] w-4 h-4" />
						</button>
					)}
				</div>
			</div>
		</div>
	);
});

export default FilterCarousel;
